import { menuDataSource } from '$lib/static';

export class MenuUtils {
	/** @type {import('$lib/types').MenuDataSource | null} */
	#menuData = $state(null);
	/** @type {boolean} */
	#resPathHasRole = $state(false);
	#title = $state('');
	#titleShort = $state('');
	/** @type {Array<import('$lib/types').MenuItem>} */
	#tempResMenuItemsForRole = [];

	// for singleton
	/** @type {MenuUtils} */
	static #instance;

	constructor() {
		if (!MenuUtils.#instance) {
			this.#menuData = menuDataSource;
			MenuUtils.#instance = this;
		}
		return MenuUtils.#instance;
	}

	/**
	 * @param {string} pathname
	 */
	pathIsPublic(pathname) {
		const res = this.pathHasRole('public', pathname);
		return res;
	}

	/**
	 * @param {string} roleName
	 * @param {string} pathname
	 */
	pathHasRole(roleName, pathname) {
		this.#tempResMenuItemsForRole = [];
		this.#pathHasRole(roleName, pathname);
		// console.log(this.#tempResMenuItemsForRole);
		return this.#tempResMenuItemsForRole.length > 0;
	}

	/**
	 * @param {string} roleName
	 * @param {string} pathname
	 * @param {import('$lib/types').SubMenu | import('$lib/types').MenuDataSource} [menuSubset]
	 */
	#pathHasRole(roleName, pathname, menuSubset) {
		if (this.#resPathHasRole === false) {
			const menuData = menuSubset ?? this.#menuData;
			const items = menuData?.items?.filter((item) => {
				if (this.isSubMenu(item)) this.#pathHasRole(roleName, pathname, item);
				else {
					if (pathname.length > item.path.length && item.path.length === 1) return false;
					else
						return (
							pathname.startsWith(item.path) &&
							pathname.includes(item.path) &&
							pathname.length >= item.path.length
						);
				}
			});

			// pour skip '/' qui est ajouté aux resultats, on prends le dernier élément
			if (items) {
				const res = items[0];

				if (res && this.isMenuItem(res) && res.allowedRoles.includes(roleName)) {
					this.#tempResMenuItemsForRole.push(res);
				}
			}
		}
	}

	/**
	 * @param {Array<import('$lib/types').MenuItem>} menuItems
	 * @returns {Array<string>}
	 */
	setMainMenuGroups(menuItems) {
		/** @type {Array<string>} */
		const res = [];
		this.#menuData?.items.forEach((item) => {
			if (
				// @ts-ignore
				item.items?.filter((item) => {
					menuItems.filter((menu) => {
						item.path === menu.path;
					})?.length > 0;
				})
			)
				res.push(item.name);
		});
		return res;
	}

	/**
	 *
	 * @param {Array<string>} roles
	 * @returns {Array<import('$lib/types').MenuItem>}
	 */
	getMenuItemsForRoles(roles) {
		this.#tempResMenuItemsForRole = [];
		this.#getMenuItemsForRoles(roles);

		return this.#tempResMenuItemsForRole;
	}

	/**
	 * @param {Array<string>} roles
	 * @param {import('$lib/types').SubMenu | import('$lib/types').MenuDataSource} [menuSubset]
	 * @param {string} [group]
	 * @returns {void}
	 */
	#getMenuItemsForRoles(roles, menuSubset, group) {
		const menuData = menuSubset ?? this.#menuData;

		menuData?.items.forEach((item) => {
			if (this.isSubMenu(item)) this.#getMenuItemsForRoles(roles, item, item.name);
			else {
				const isOk = roles?.filter((role) => item.allowedRoles.includes(role)).length > 0;
				if (isOk && item.path !== '/') {
					if (group) item.group = group;
					else item.group = 'root';
					this.#tempResMenuItemsForRole.push(item);
				}
			}
		});
	}

	/**
	 * @param {string} pathname
	 * @param {import('$lib/types').SubMenu | import('$lib/types').MenuDataSource} [menuSubset]
	 * @returns {void}
	 */
	#setTitle(pathname, menuSubset) {
		const menuData = menuSubset ?? this.#menuData;
		const title = menuData?.items?.filter((item) => {
			if (this.isSubMenu(item)) return this.#setTitle(pathname, item);
			else {
				if (pathname.length > item.path.length && item.path.length === 1) return false;
				else
					return (
						pathname.startsWith(item.path) &&
						pathname.includes(item.path) &&
						pathname.length >= item.path.length
					);
			}
		})[0];

		if (title) {
			this.#titleShort = title.name;
			// @ts-ignore
			this.#title = `${title.name} - ${APP_NAME}`;
		}
	}

	/**
	 * @param {string} groupeName
	 * @param {string} currentPathName
	 * @returns {boolean}
	 */
	currentPathInGroup(groupeName, currentPathName) {
		const res = this.#menuData?.items.filter((item) => {
			if (
				this.isSubMenu(item) &&
				// @ts-ignore
				item.items.filter((/** @type {import('$lib/types').MenuItem} */ l2item) => {
					return l2item.path === currentPathName;
				})?.length > 0 &&
				item.name === groupeName
			)
				return true;
			else return false;
		});

		if (res && res.length > 0) return true;
		else return false;
	}

	/**
	 * Type guard to check if an item is a SubMenu.
	 * @param {import('$lib/types').SubMenu | import('$lib/types').MenuItem} item - The menu item or submenu.
	 * @return {item is import('$lib/types').SubMenu} - True if the menu is a SubMenu.
	 */
	isSubMenu(item) {
		return 'items' in item;
	}

	/**
	 * Type guard to check if an item is a SubMenu.
	 * @param {import('$lib/types').SubMenu | import('$lib/types').MenuItem} item - The menu item or submenu.
	 * @return {item is import('$lib/types').MenuItem} - True if the menu is a SubMenu.
	 */
	isMenuItem(item) {
		return !('items' in item);
	}

	/**
	 * @param {string} pathname
	 * @returns {void}
	 */
	setTitle(pathname) {
		this.#setTitle(pathname);
	}

	get title() {
		return this.#title;
	}

	get titleShort() {
		return this.#titleShort;
	}

	/** @returns {import('$lib/types').MenuDataSource | null} */
	get menuData() {
		return this.#menuData;
	}
}
